<div class="page-container">
  <div class="back-button-wrapper">
    <p-button
      icon="pi pi-arrow-left"
      label="Back to Menu Items"
      severity="secondary"
      [queryParams]="backQueryParams()"
      [routerLink]="backCommands()"
    ></p-button>
  </div>

  @if (entree(); as detail) {
    <div class="action-header">
      <div class="action-header-left">
        @if (detail.imageUrl) {
          <img class="thumb" appImageFallback [alt]="detail.name" [src]="detail.imageUrl" />
        } @else {
          <div class="thumb thumb--placeholder">
            <i class="pi pi-image"></i>
          </div>
        }
        <div class="title-block">
          <h1 class="entree-title">{{ detail.name }}</h1>
          @if (detail.marketingName) {
            <p class="marketing-name">{{ detail.marketingName }}</p>
          }
          @if (detail.status) {
            <p-tag [severity]="getStatusSeverity(detail.status)" [value]="detail.status"></p-tag>
          }
        </div>
      </div>
      <div class="action-header-right">
        <p-button
          icon="pi pi-angle-double-right"
          label="Next pending"
          severity="secondary"
          [disabled]="submitting() || advancing()"
          [text]="true"
          (onClick)="goToNextPending()"
        ></p-button>
        <p-button
          icon="pi pi-times"
          label="Reject"
          severity="danger"
          [disabled]="submitting()"
          (onClick)="openReject()"
        ></p-button>
        <p-button
          icon="pi pi-check"
          label="Approve"
          severity="primary"
          [disabled]="submitting()"
          (onClick)="openApprove()"
        ></p-button>
      </div>
    </div>

    <div class="detail-content">
      <p-card header="Details" styleClass="detail-card">
        <div class="detail-row">
          <strong>Healthy Gourmet Kitchen:</strong>
          <span>
            <a class="producer-link" [routerLink]="['/producers', detail.producerId]">
              {{ producerName() || detail.producerId }}
            </a>
          </span>
        </div>
        <div class="detail-row">
          <strong>Target market:</strong>
          <span>
            {{ targetMarketName() || '—' }}
            @if (usingFallbackPhenotype()) {
              <span class="empty-state">(not set — showing scores for this phenotype)</span>
            }
          </span>
        </div>
        <div class="detail-row">
          <strong>Spice level:</strong>
          <span>{{ detail.spiceLevel }}</span>
        </div>
        <div class="detail-row">
          <strong>Fulfillment:</strong>
          <span class="chip-row">
            @for (mode of fulfillmentModes(); track mode) {
              <p-tag severity="info" [value]="mode"></p-tag>
            } @empty {
              <span class="empty-state">—</span>
            }
          </span>
        </div>
        @for (group of scheduleAndSaveInventory(); track group.establishmentGuid) {
          <div class="detail-row">
            <strong>Weekly inventory:</strong>
            <span>
              @if (scheduleAndSaveInventory().length > 1) {
                <span class="empty-state">{{ group.establishmentGuid.slice(0, 8) }} —</span>
              }
              {{ formatInventory(group.maxDailyAmounts) }}
            </span>
          </div>
        }
        @if (missingScheduleAndSaveStock()) {
          <p-message severity="warn" styleClass="inventory-warning" [text]="approveWarning()!"></p-message>
        }
        @if (detail.rejectionReason) {
          <div class="detail-row">
            <strong>Rejection reason:</strong>
            <span>{{ detail.rejectionReason }}</span>
          </div>
        }
      </p-card>

      @if (pricing().length) {
        <p-card header="Proposed Pricing" styleClass="detail-card">
          @for (group of pricing(); track group.establishmentGuid) {
            @if (pricing().length > 1) {
              <div class="targets-section-header">Establishment {{ group.establishmentGuid.slice(0, 8) }}</div>
            }
            @for (price of group.prices; track price.portionSize) {
              <div class="detail-row">
                <strong>{{ price.portionSize }}:</strong>
                <span>{{ price.basePrice | currency }}</span>
              </div>
            }
          }
        </p-card>
      }

      <p-card header="Entree Inventory" styleClass="detail-card">
        <app-menu-item-inventory
          [entreeId]="detail.id"
          [establishments]="offeringEstablishments()"
        ></app-menu-item-inventory>
      </p-card>

      <p-card header="Sizes & Availability ({{ detail.sizes.length }})" styleClass="detail-card">
        @for (size of detail.sizes; track size.entreeSizeId) {
          <p-panel styleClass="size-panel" [header]="size.portionSize" [toggleable]="true">
            <div class="detail-row">
              <strong>Allergens:</strong>
              <span class="chip-row">
                @for (allergen of size.allergens ?? []; track allergen.name) {
                  <p-tag severity="warn" [value]="allergen.name"></p-tag>
                } @empty {
                  <span class="empty-state">None declared</span>
                }
              </span>
            </div>
            <div class="detail-row">
              <strong>Toxins:</strong>
              <span class="chip-row">
                @for (toxin of size.toxins ?? []; track toxin.name) {
                  <p-tag severity="danger" [value]="toxin.name"></p-tag>
                } @empty {
                  <span class="empty-state">None declared</span>
                }
              </span>
            </div>

            @let targets = nutrientTargetsForSize(size.entreeSizeId);
            <div class="nutrient-targets">
              <div class="targets-header">
                <span [class]="'target-badge target-badge--overall ' + scoreBadgeClass(targets.overallScore)">
                  {{ formatScore(targets.overallScore) }}
                </span>
                <span class="targets-title">Nutrient Targets</span>
              </div>
              @for (section of targets.sections; track $index) {
                @if (section.header) {
                  <div class="targets-section-header">{{ section.header }}</div>
                }
                @for (category of section.categories; track category.scoringCategoryId) {
                  <div class="target-card">
                    <button
                      type="button"
                      class="target-card-header"
                      (click)="toggleTarget(size.entreeSizeId, category.scoringCategoryId)"
                    >
                      <span [class]="'target-badge ' + scoreBadgeClass(category.score)">
                        {{ formatScore(category.score) }}
                      </span>
                      <span class="target-name">{{ category.displayName }}</span>
                      <i
                        class="pi target-chevron"
                        [class.pi-chevron-down]="!isTargetExpanded(size.entreeSizeId, category.scoringCategoryId)"
                        [class.pi-chevron-up]="isTargetExpanded(size.entreeSizeId, category.scoringCategoryId)"
                      ></i>
                    </button>
                    @if (isTargetExpanded(size.entreeSizeId, category.scoringCategoryId)) {
                      <div class="target-card-body">
                        @if (category.nutrients.length) {
                          <ul class="nutrient-list">
                            @for (nutrient of category.nutrients; track nutrient.nutrientId) {
                              <li class="nutrient-row">
                                <span class="nutrient-name">{{ nutrient.nutrientName }}</span>
                                <span class="nutrient-value">
                                  {{ nutrient.nutrientQuantity | number: '1.0-2' }} {{ nutrient.nutrientUnitOfMeasure }}
                                </span>
                              </li>
                            }
                          </ul>
                        } @else {
                          <p class="empty-state">No nutrient detail.</p>
                        }
                      </div>
                    }
                  </div>
                }
              }
            </div>
          </p-panel>
        } @empty {
          <p class="empty-state">No sizes registered.</p>
        }
      </p-card>
    </div>

    <app-approve-confirm-dialog
      [entreeName]="entreeName()"
      [submitting]="submitting()"
      [warning]="approveWarning()"
      [(visible)]="approveDialogVisible"
      (approve)="onApprove()"
    ></app-approve-confirm-dialog>

    <app-reject-reason-dialog
      [entreeName]="entreeName()"
      [submitting]="submitting()"
      [(visible)]="rejectDialogVisible"
      (reject)="onReject($event)"
    ></app-reject-reason-dialog>
  } @else if (loading()) {
    <div class="loading-state">
      <p-skeleton height="6rem" width="100%"></p-skeleton>
      <p-skeleton height="12rem" styleClass="mt-3" width="100%"></p-skeleton>
      <p-skeleton height="12rem" styleClass="mt-3" width="100%"></p-skeleton>
    </div>
  } @else if (error()) {
    <p-message severity="error" text="Failed to load menu item. Please try again."></p-message>
  } @else {
    <app-page-header title="Menu Item Detail"></app-page-header>
  }
</div>
